"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { useLocale } from "next-intl";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CalendarIcon,
  UserIcon,
  ClockIcon,
  ShareIcon,
  ArrowLeftIcon,
  TagIcon,
  CalendarPlusIcon,
} from "lucide-react";

import { db } from "@/lib/firebase.client";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  DocumentData,
} from "firebase/firestore";

import { cn } from "@/lib/utils";
import { buildNewsDetailPath, getLocalizedPathname, parseContentSegment } from "@/lib/site";

/* ---------------- Types ---------------- */
type Locale = "en" | "fr";
type Localized<T> = Partial<Record<Locale, T>>;

type RenderBlock =
  | { type: "h1" | "h2" | "h3"; text: string; className?: string }
  | { type: "p"; text: string; className?: string }
  | {
      type: "image";
      url: string;
      alt?: string;
      className?: string;
      width?: number;
      height?: number;
    }
  | { type: "html"; html: string; className?: string };

type FSBlockRaw =
  | { type: "paragraph"; text?: string; className?: string }
  | { type: "heading"; level?: 1 | 2 | 3; text?: string; className?: string }
  | {
      type: "image";
      src?: string;
      alt?: string;
      className?: string;
      width?: number;
      height?: number;
      mime?: string;
    }
  | { type: "html"; html?: string; className?: string };

type NewsDoc = {
  title: Localized<string>;
  excerpt: Localized<string>;
  author?: string;
  date: string;
  readTime?: string;
  image?: string;
  tags?: string[];
  isPublished: boolean;
  categorySlugs: string[];
  contentBlocks?: Localized<FSBlockRaw[]>;
  blocks?: Localized<RenderBlock[]>;
  content?: Localized<string> | string;
};

type ProductDoc = {
  id: string;
  active?: boolean; // EVENT_ACTIVE_FIELD
  product_type?: string; // EVENT_TYPE_FIELD
  product_name?: string;
  product_name_en?: string;
  product_description?: string; // HTML
  product_description_en?: string; // HTML
  start_date?: string;
  creation_date?: string;
  image?: string;
};
const NEWS_COLL = "website-news";

/* --------- Events (products) wiring --------- */
const EVENTS_COLL = "products"; // ⬅️ your products collection
const EVENT_TYPE_FIELD = "product_type";
const EVENT_TYPE_VALUE = "UNCOMMON_EVENT";
const EVENT_ACTIVE_FIELD = "active";
const EVENT_CATEGORY_SLUG = "events";
const EVENT_ID_PREFIX = "event_";

/* ---------------- Defaults ---------------- */
const DEFAULTS = {
  h1: "mt-10 mb-4 text-3xl md:text-4xl font-bold tracking-tight",
  h2: "mt-10 mb-3 text-2xl md:text-3xl font-semibold",
  h3: "mt-8 mb-2 text-xl md:text-2xl font-semibold",
  p: "my-6 text-base leading-7 text-neutral-800 dark:text-neutral-200",
  figure: "my-8",
  htmlWrapper: "prose prose-lg max-w-none dark:prose-invert",
  figcaption: "mt-2 text-center text-sm text-muted-foreground",
};

const EVENT_DESCRIPTION_CLASS =
  "whitespace-pre-line leading-7 [&_div]:my-6 [&_div:first-child]:mt-0 [&_div:last-child]:mb-0 [&_p]:my-6 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0";

/* ---------------- Helpers ---------------- */
function pickLocalized(obj: Localized<string> | undefined, locale: Locale) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[locale] ?? obj.en ?? obj.fr ?? "";
}

function normalizeBlocks(raw?: FSBlockRaw[]): RenderBlock[] {
  if (!raw?.length) return [];
  return raw
    .map<RenderBlock | null>((b) => {
      switch (b.type) {
        case "paragraph":
          if (!b.text) return null;
          return {
            type: "p",
            text: b.text,
            className: cn(DEFAULTS.p, b.className),
          };
        case "heading": {
          const lvl = b.level ?? 2;
          const text = b.text ?? "";
          if (!text) return null;
          if (lvl === 1)
            return {
              type: "h1",
              text,
              className: cn(DEFAULTS.h1, b.className),
            };
          if (lvl === 3)
            return {
              type: "h3",
              text,
              className: cn(DEFAULTS.h3, b.className),
            };
          return { type: "h2", text, className: cn(DEFAULTS.h2, b.className) };
        }
        case "image":
          if (!b.src) return null;
          return {
            type: "image",
            url: b.src,
            alt: b.alt,
            className: cn(DEFAULTS.figure, b.className),
            width: b.width,
            height: b.height,
          };
        case "html":
          if (!b.html) return null;
          return {
            type: "html",
            html: b.html,
            className: cn(DEFAULTS.htmlWrapper, b.className),
          };
        default:
          return null;
      }
    })
    .filter(Boolean) as RenderBlock[];
}

function pickRenderableBlocks(post: NewsDoc, locale: Locale): RenderBlock[] {
  const raw =
    post.contentBlocks?.[locale] ??
    post.contentBlocks?.en ??
    post.contentBlocks?.fr;
  const normalized = normalizeBlocks(raw);
  if (normalized.length) return normalized;

  const old = post.blocks?.[locale] ?? post.blocks?.en ?? post.blocks?.fr ?? [];
  return old.map((b) => {
    if (b.type === "p") return { ...b, className: cn(DEFAULTS.p, b.className) };
    if (b.type === "h1")
      return { ...b, className: cn(DEFAULTS.h1, b.className) };
    if (b.type === "h2")
      return { ...b, className: cn(DEFAULTS.h2, b.className) };
    if (b.type === "h3")
      return { ...b, className: cn(DEFAULTS.h3, b.className) };
    if (b.type === "image")
      return { ...b, className: cn(DEFAULTS.figure, b.className) };
    if (b.type === "html")
      return { ...b, className: cn(DEFAULTS.htmlWrapper, b.className) };
    return b;
  });
}

function RenderBlocks({ blocks }: { blocks: RenderBlock[] }) {
  if (!blocks?.length) return null;
  return (
    <div className="prose prose-lg max-w-none dark:prose-invert">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "h1":
            return (
              <h1 key={i} className={cn(DEFAULTS.h1, b.className)}>
                {b.text}
              </h1>
            );
          case "h2":
            return (
              <h2 key={i} className={cn(DEFAULTS.h2, b.className)}>
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={i} className={cn(DEFAULTS.h3, b.className)}>
                {b.text}
              </h3>
            );
          case "p":
            return (
              <p key={i} className={cn(DEFAULTS.p, b.className)}>
                {b.text}
              </p>
            );
          case "image":
            return (
              <figure key={i} className={cn(DEFAULTS.figure, b.className)}>
                <Image
                  src={b.url}
                  alt={b.alt ?? ""}
                  width={b.width ?? 1280}
                  height={b.height ?? 720}
                  className="h-auto w-full rounded-2xl object-cover shadow-lg ring-1 ring-black/5 dark:ring-white/10"
                />
                {b.alt ? (
                  <figcaption className={DEFAULTS.figcaption}>
                    {b.alt}
                  </figcaption>
                ) : null}
              </figure>
            );
          case "html":
            return (
              <div
                key={i}
                className={cn(DEFAULTS.htmlWrapper, b.className)}
                dangerouslySetInnerHTML={{ __html: b.html }}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

function pickRandom<T>(arr: T[], n: number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
}
const isEventId = (id: string) => id.startsWith(EVENT_ID_PREFIX);

type EventRegistrationValues = {
  name: string;
  email: string;
  phone: string;
};

type EventRegistrationErrors = Partial<
  Record<keyof EventRegistrationValues | "form", string>
>;

const EMPTY_REGISTRATION: EventRegistrationValues = {
  name: "",
  email: "",
  phone: "",
};

function EventRegistrationDialog({
  locale,
  eventTitle,
  eventDate,
  eventId,
}: {
  locale: Locale;
  eventTitle: string;
  eventDate: string;
  eventId: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [values, setValues] =
    React.useState<EventRegistrationValues>(EMPTY_REGISTRATION);
  const [errors, setErrors] = React.useState<EventRegistrationErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const isFr = locale === "fr";
  const labels = {
    open: isFr ? "S'inscrire" : "Register",
    title: isFr ? "S'inscrire à l'événement" : "Register for event",
    description: isFr
      ? "Laissez vos coordonnées afin que l'équipe Uncommon 12 puisse revenir vers vous."
      : "Leave your details so the Uncommon 12 team can follow up with you.",
    name: isFr ? "Nom complet" : "Full name",
    email: "Email",
    phone: isFr ? "Téléphone" : "Phone",
    cancel: isFr ? "Annuler" : "Cancel",
    register: isFr ? "S'inscrire" : "Register",
    sending: isFr ? "Envoi..." : "Sending...",
    success: isFr
      ? "Votre demande d'inscription a été envoyée."
      : "Your registration request has been sent.",
    required: isFr ? "Champ requis" : "Required field",
    invalidEmail: isFr ? "Email invalide" : "Invalid email",
    error: isFr ? "Envoi impossible" : "Unable to send",
  };

  const resetForm = React.useCallback(() => {
    setValues(EMPTY_REGISTRATION);
    setErrors({});
    setIsSubmitting(false);
    setSuccess(false);
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) resetForm();
  };

  const updateField =
    (field: keyof EventRegistrationValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((current) => ({ ...current, [field]: event.target.value }));
      setErrors((current) => ({
        ...current,
        [field]: undefined,
        form: undefined,
      }));
    };

  const validate = () => {
    const nextErrors: EventRegistrationErrors = {};
    if (!values.name.trim()) nextErrors.name = labels.required;
    if (!values.email.trim()) {
      nextErrors.email = labels.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = labels.invalidEmail;
    }
    if (!values.phone.trim()) nextErrors.phone = labels.required;
    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: "contact@uncommon12.ch",
          subject: `Event registration interest: ${eventTitle}`,
          bodyTemplate: "event-registration",
          layoutTemplate: "contact",
          templateData: {
            event_title: eventTitle,
            event_date: eventDate,
            event_id: eventId,
            event_url: window.location.href,
            name: values.name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
          },
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const rawError = payload?.error;
        const message =
          typeof rawError === "string"
            ? rawError
            : rawError?.message ?? rawError?.response ?? labels.error;
        throw new Error(message);
      }

      setSuccess(true);
      setValues(EMPTY_REGISTRATION);
      window.setTimeout(() => {
        setOpen(false);
        resetForm();
      }, 1500);
    } catch (error) {
      setErrors({
        form: error instanceof Error ? error.message : labels.error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button className="flex items-center gap-2" onClick={() => setOpen(true)}>
        <CalendarPlusIcon className="h-4 w-4" />
        {labels.open}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
            <DialogDescription>{labels.description}</DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="rounded-md bg-green-500/10 p-4 text-sm text-green-700 dark:text-green-300">
              {labels.success}
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="event-registration-name">{labels.name}</Label>
                <Input
                  id="event-registration-name"
                  value={values.name}
                  onChange={updateField("name")}
                  aria-invalid={Boolean(errors.name)}
                  autoComplete="name"
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="event-registration-email">{labels.email}</Label>
                <Input
                  id="event-registration-email"
                  type="email"
                  value={values.email}
                  onChange={updateField("email")}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="event-registration-phone">{labels.phone}</Label>
                <Input
                  id="event-registration-phone"
                  type="tel"
                  value={values.phone}
                  onChange={updateField("phone")}
                  aria-invalid={Boolean(errors.phone)}
                  autoComplete="tel"
                />
                {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
              </div>

              {errors.form && (
                <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
                  {errors.form}
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSubmitting}
                >
                  {labels.cancel}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? labels.sending : labels.register}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ArticleActions({
  locale,
  title,
  excerpt,
  isProductEvent,
  eventDate,
  eventId,
}: {
  locale: Locale;
  title: string;
  excerpt: string;
  isProductEvent: boolean;
  eventDate: string;
  eventId: string;
}) {
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title,
        text: excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="flex gap-3 sm:flex-row sm:items-center justify-between flex-wrap">
      <Link href={getLocalizedPathname(locale, "/news")}>
        <Button variant="outline" className="flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" />
          {locale === "fr" ? "Retour aux actualités" : "Back to news"}
        </Button>
      </Link>
      <div className="flex shrink-0 flex-nowrap items-center gap-2">
        {isProductEvent && (
          <EventRegistrationDialog
            locale={locale}
            eventTitle={title}
            eventDate={eventDate}
            eventId={eventId}
          />
        )}
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={handleShare}
        >
          <ShareIcon className="h-4 w-4" />
          {locale === "fr" ? "Partager" : "Share"}
        </Button>
      </div>
    </div>
  );
}

/* ---- Map an event (product) doc → NewsDoc shape ---- */
function eventToNewsDoc(d: ProductDoc): NewsDoc & { _id: string } {
  // fields from your screenshot
  const titleFr = d.product_name as string | undefined;
  const titleEn = d.product_name_en as string | undefined;
  const descFr = d.product_description as string | undefined; // HTML
  const descEn = d.product_description_en as string | undefined; // HTML
  const start = (d.start_date as string | undefined) ?? d.creation_date;
  const image = d.image as string | undefined;

  const htmlBlockFr: FSBlockRaw = {
    type: "html",
    html: descFr,
    className: EVENT_DESCRIPTION_CLASS,
  };
  const htmlBlockEn: FSBlockRaw = {
    type: "html",
    html: descEn,
    className: EVENT_DESCRIPTION_CLASS,
  };
  function formatEventDateLabel(iso?: string, locale: "fr" | "en" = "fr") {
    if (!iso) return "";
    const loc = locale === "fr" ? "fr-FR" : "en-GB";
    return new Date(iso).toLocaleDateString(loc, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  }
  return {
    _id: `${EVENT_ID_PREFIX}${d.id}`,
    title: { fr: titleFr ?? "", en: titleEn ?? "" },
    excerpt: {
      fr: "📅 " + formatEventDateLabel(start, "fr"),
      en: "📅 " + formatEventDateLabel(start, "en"),
    },
    author: "Uncommon 12",
    date: start ?? new Date().toISOString(),
    image,
    tags: ["event"],
    isPublished: true,
    categorySlugs: [EVENT_CATEGORY_SLUG],
    contentBlocks: {
      fr: descFr ? [htmlBlockFr] : [],
      en: descEn ? [htmlBlockEn] : [],
    },
  };
}

/* ---------------- Page ---------------- */
export default function NewsPostPage() {
  const params = useParams<{ id: string }>();
  const router = useRouter();
  const locale = (useLocale() as Locale) ?? "en";

  const [post, setPost] = React.useState<(NewsDoc & { _id: string }) | null>(
    null
  );
  const [related, setRelated] = React.useState<(NewsDoc & { _id: string })[]>(
    []
  );
  const [loading, setLoading] = React.useState(true);
  const [missing, setMissing] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const id = parseContentSegment(params.id).id;
        const eventMode = isEventId(id);
        let data: (NewsDoc & { _id: string }) | null = null;

        if (eventMode) {
          // fetch product (event)
          const rawId = id.replace(EVENT_ID_PREFIX, "");
          const eref = doc(db, EVENTS_COLL, rawId);
          const esnap = await getDoc(eref);
          if (!esnap.exists()) throw new Error("Event not found");
          const edata: ProductDoc = {
            id: esnap.id,
            ...(esnap.data() as DocumentData),
          };
          if (!edata.active || edata.product_type !== EVENT_TYPE_VALUE) {
            throw new Error("Event inactive or wrong type");
          }
          data = eventToNewsDoc(edata);
        } else {
          // fetch regular news
          const ref = doc(db, NEWS_COLL, id);
          const snap = await getDoc(ref);
          if (!snap.exists()) throw new Error("News not found");
          const nd = {
            _id: snap.id,
            ...(snap.data() as NewsDoc),
          } as NewsDoc & { _id: string };
          if (!nd.isPublished) throw new Error("Unpublished");
          data = nd;
        }

        if (mounted) setPost(data);

        // ---- Related: get a mix of other news + other active events ----
        const relatedPool: (NewsDoc & { _id: string })[] = [];

        // 1) news
        const nq = query(
          collection(db, NEWS_COLL),
          where("isPublished", "==", true),
          orderBy("date", "desc"),
          limit(40)
        );
        const ns = await getDocs(nq);
        ns.forEach((d) =>
          relatedPool.push({
            _id: d.id,
            ...(d.data() as NewsDoc),
          } as NewsDoc & { _id: string })
        );

        // 2) events
        const eq = query(
          collection(db, EVENTS_COLL),
          where(EVENT_TYPE_FIELD, "==", EVENT_TYPE_VALUE),
          where(EVENT_ACTIVE_FIELD, "==", true),
          // orderBy("start_date","desc") // optional if you have index
          limit(40)
        );
        const es = await getDocs(eq);
        es.forEach((d) => {
          const mapped = eventToNewsDoc({
            id: d.id,
            ...(d.data() as DocumentData),
          } as ProductDoc);
          relatedPool.push(mapped);
        });

        // filter out self
        const pool = relatedPool.filter((p) => p._id !== data._id);

        // prefer same category overlap (works for events & news)
        const sameCat = pool.filter((p) =>
          p.categorySlugs?.some((s) => data!.categorySlugs?.includes(s))
        );

        let chosen = pickRandom(sameCat, 3);
        if (chosen.length < 3) {
          const rest = pool.filter((p) => !chosen.some((c) => c._id === p._id));
          chosen = [...chosen, ...pickRandom(rest, 3 - chosen.length)];
        }

        if (mounted) setRelated(chosen);
      } catch {
        if (mounted) setMissing(true);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [params.id]);

  React.useEffect(() => {
    if (missing) router.replace(getLocalizedPathname(locale, "/news"));
  }, [locale, missing, router]);

  if (missing) return <div className="min-h-[40vh]" />;

  if (loading || !post) {
    return (
      <div className="min-h-[60vh] w-full">
        <div className="h-[60vh] w-full animate-pulse bg-muted" />
        <div className="mx-auto max-w-4xl px-4 py-16">
          <div className="mb-6 h-8 w-2/3 animate-pulse rounded bg-muted" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-5 animate-pulse rounded bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isEvent = post.categorySlugs?.includes(EVENT_CATEGORY_SLUG);
  const isProductEvent = isEventId(post._id);
  const title = pickLocalized(post.title, locale);
  const excerpt = pickLocalized(post.excerpt, locale);
  const formattedDate = new Date(post.date).toLocaleDateString(
    locale === "fr" ? "fr-FR" : "en-GB"
  );
  const blocks = pickRenderableBlocks(post, locale);
  const legacyHtml =
    typeof post.content === "string"
      ? post.content
      : pickLocalized(post.content as Localized<string> | undefined, locale);

  return (
    <div className="min-h-screen font-[family-name:var(--font-geist-sans)]">
      <div className="w-full">
        {/* Hero Section */}
        <div className="relative h-[60vh] overflow-hidden">
          <div className="absolute inset-0">
            {post.image ? (
              <Image
                src={post.image}
                alt={title || "News cover"}
                fill
                sizes="100vw"
                priority
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div className="h-full w-full bg-muted" />
            )}
          </div>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto w-full max-w-4xl px-4 pb-12">
              <Breadcrumb className="mb-6">
                <BreadcrumbList className="text-white/80">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        href={getLocalizedPathname(locale, "/")}
                        className="text-white/80 hover:text-white"
                      >
                        Home
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-white/60" />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        href={getLocalizedPathname(locale, "/news")}
                        className="text-white/80 hover:text-white"
                      >
                        News
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-white/60" />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-white">
                      {title}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              {!!post.categorySlugs?.length && (
                <Badge className="mb-4 bg-[#FCD2C3] text-black">
                  {isEvent
                    ? locale === "fr"
                      ? "Événements"
                      : "Events"
                    : post.categorySlugs[0]}
                </Badge>
              )}

              <h1 className="mb-4 text-4xl font-bold leading-tight text-white md:text-5xl">
                {title}
              </h1>

              {excerpt && (
                <p className="mb-6 max-w-2xl text-xl text-white/90">
                  {excerpt}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  <span>{post.author || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
                {post.readTime && !isEvent && (
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4" />
                    <span>{post.readTime}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="mx-auto max-w-4xl px-4 py-16">
          <div className="flex flex-col gap-12 lg:flex-row">
            {/* Main Content */}
            <article className="flex-1">
              <div className="mb-8 border-b border-neutral-200 pb-8 dark:border-neutral-800">
                <ArticleActions
                  locale={locale}
                  title={title}
                  excerpt={excerpt}
                  isProductEvent={isProductEvent}
                  eventDate={formattedDate}
                  eventId={post._id}
                />
              </div>

              {blocks.length ? (
                <RenderBlocks blocks={blocks} />
              ) : legacyHtml ? (
                <div
                  className={DEFAULTS.htmlWrapper}
                  dangerouslySetInnerHTML={{ __html: legacyHtml }}
                />
              ) : (
                <p className="text-muted-foreground">
                  {locale === "fr"
                    ? "Contenu à venir…"
                    : "Content coming soon…"}
                </p>
              )}

              {!!post.tags?.length && (
                <div className="mt-12 border-t border-neutral-200 pt-8 dark:border-neutral-800">
                  <div className="mb-4 flex items-center gap-2">
                    <TagIcon className="h-4 w-4 text-neutral-500" />
                    <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                      Tags:
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-neutral-200 pt-8 dark:border-neutral-800">
                <ArticleActions
                  locale={locale}
                  title={title}
                  excerpt={excerpt}
                  isProductEvent={isProductEvent}
                  eventDate={formattedDate}
                  eventId={post._id}
                />
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80">
              <div className="sticky top-32 space-y-8">
                <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-neutral-900">
                  <h3 className="mb-4 text-lg font-bold text-black dark:text-white">
                    {locale === "fr"
                      ? "À propos de l’auteur"
                      : "About the author"}
                  </h3>
                  <div className="mb-4 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#FCD2C3] to-[#BBD8E6]">
                      <span className="text-lg font-bold text-black">
                        {(post.author || "U12")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-black dark:text-white">
                        {post.author || "—"}
                      </p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {locale === "fr"
                          ? "Membre Uncommon 12"
                          : "Uncommon 12 member"}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {locale === "fr"
                      ? "Passionné par l’art de vivre genevois et les connexions authentiques."
                      : "Passionate about Geneva lifestyle and authentic connections."}
                  </p>
                </div>

                {!!related.length && (
                  <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-neutral-900">
                    <h3 className="mb-4 text-lg font-bold text-black dark:text-white">
                      {locale === "fr"
                        ? "Articles similaires"
                        : "Related posts"}
                    </h3>
                    <div className="space-y-4">
                      {related.map((rp) => (
                        <Link
                          key={rp._id}
                          href={buildNewsDetailPath(locale, rp._id, rp.title)}
                        >
                          <div className="group cursor-pointer">
                            <div className="relative mb-3 aspect-video overflow-hidden rounded-lg">
                              {rp.image ? (
                                <Image
                                  src={rp.image}
                                  alt={pickLocalized(rp.title, locale)}
                                  fill
                                  sizes="320px"
                                  style={{ objectFit: "cover" }}
                                  className="transition-transform duration-300 group-hover:scale-105"
                                />
                              ) : (
                                <div className="h-full w-full bg-muted" />
                              )}
                            </div>
                            <h4 className="mb-2 line-clamp-2 text-sm font-medium text-black transition-colors group-hover:text-[#FCD2C3] dark:text-white">
                              {pickLocalized(rp.title, locale)}
                            </h4>
                            <p className="text-xs text-neutral-600 dark:text-neutral-400">
                              {new Date(rp.date).toLocaleDateString(
                                locale === "fr" ? "fr-FR" : "en-GB"
                              )}{" "}
                              • {rp.readTime ?? "—"}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
